import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { HiTrash } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import { getLocalPlanPaymentByPlanId, deleteLocalPlanPayment } from 'crud/configuration_api/local_plan_payments';
import MiniLoadingSpinner from 'components/Spinners/MiniLoadingSpinner';
import './UpdateLocalPlanPayment.scss';

interface UpdateLocalPlanPaymentProps {
  show: boolean;
  onHide: () => void;
  localPlanId: number | null;
  localName: string;
  onPaymentDeleted: () => void;
}

interface Payment {
  payment_id: number;
  local_id: number;
  amount: number;
  payment_date: string;
  payment_method: string | null;
  notes: string | null;
  days_since_last_payment: number | null;
  local_plans_payments_metadata?: {
    created_by?: string;
    [key: string]: any;
  };
}

const UpdateLocalPlanPayment: React.FC<UpdateLocalPlanPaymentProps> = ({ 
  show, 
  onHide, 
  localPlanId, 
  localName,
  onPaymentDeleted 
}) => {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [deletingPaymentId, setDeletingPaymentId] = useState<number | null>(null);

  useEffect(() => {
    if (show && localPlanId) {
      fetchPayments();
    }
  }, [show, localPlanId]);

  const fetchPayments = async () => {
    if (!localPlanId) return;
    
    setLoading(true);
    try {
      const response = await getLocalPlanPaymentByPlanId(localPlanId);
      if (response.status === 200) {
        // Sort by payment_date descending (most recent first)
        const sortedPayments = response.data.sort((a: Payment, b: Payment) => 
          new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
        );
        setPayments(sortedPayments);
      }
    } catch (err: any) {
      console.error('Error fetching payments:', err);
      if (err?.response?.status !== 404) {
        toast.error('Error al cargar el historial de pagos');
      } else {
        setPayments([]);
      }
    }
    setLoading(false);
  };

  const handleDeletePayment = async (paymentId: number) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este pago?')) {
      return;
    }

    setDeletingPaymentId(paymentId);
    try {
      const response = await deleteLocalPlanPayment(paymentId);
      if ((response as any).status === 200) {
        toast.success('Pago eliminado exitosamente');
        setPayments(prev => prev.filter(p => p.payment_id !== paymentId));
        onPaymentDeleted();
      } else {
        toast.error('Error al eliminar el pago');
      }
    } catch (err: any) {
      console.error('Error deleting payment:', err);
      const errorMsg = err?.response?.data?.message || 'Error al eliminar el pago';
      toast.error(errorMsg);
    }
    setDeletingPaymentId(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateTotal = () => {
    return payments.reduce((sum, payment) => sum + payment.amount, 0);
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      centered
      size="lg"
      className="local-plan-payment-modal"
      backdrop="static"
    >
      <Modal.Header closeButton={false}>
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">Historial de Pagos</h2>
          <p className="form-modal-subtitle">{localName}</p>
        </div>
        <button 
          className="modal-close-button"
          onClick={onHide}
          disabled={loading}
        >
          <X size={20} />
        </button>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <MiniLoadingSpinner />
            <p style={{ marginTop: '1rem', color: 'var(--text-color-muted)' }}>
              Cargando historial de pagos...
            </p>
          </div>
        ) : payments.length === 0 ? (
          <div className="no-payments-message">
            <p>No hay pagos registrados para este local.</p>
          </div>
        ) : (
          <>
            <div className="payments-table-container">
              <table className="payments-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Monto</th>
                    <th>Método</th>
                    <th>Creado por</th>
                    <th>Días desde último pago</th>
                    <th>Notas</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.payment_id}>
                      <td>{formatDate(payment.payment_date)}</td>
                      <td className="amount-cell">{formatCurrency(payment.amount)}</td>
                      <td>{payment.payment_method || '-'}</td>
                      <td>{payment.local_plans_payments_metadata?.created_by || '-'}</td>
                      <td className="days-cell">
                        {payment.days_since_last_payment !== null 
                          ? `${payment.days_since_last_payment} días` 
                          : 'Primer pago'}
                      </td>
                      <td className="notes-cell">{payment.notes || '-'}</td>
                      <td>
                        <button
                          className="delete-payment-button"
                          onClick={() => handleDeletePayment(payment.payment_id)}
                          disabled={deletingPaymentId === payment.payment_id}
                          title="Eliminar pago"
                        >
                          {deletingPaymentId === payment.payment_id ? (
                            <MiniLoadingSpinner />
                          ) : (
                            <HiTrash size={18} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="payments-summary">
              <div className="summary-item">
                <span className="summary-label">Total de Pagos:</span>
                <span className="summary-value">{payments.length}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Monto Total:</span>
                <span className="summary-value total-amount">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn local-plan-payment-btn close-btn"
          onClick={onHide}
        >
          Cerrar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateLocalPlanPayment;
